import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Edit2, Trash2, Split, Merge } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SentenceManagementProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sentences: string[];
  onSentencesChange: (sentences: string[]) => void;
  onIdentifyVerbs: () => void;
  onProcessArticle: () => void;
  onEditVerbs?: () => void;
  isProcessing: boolean;
  isIdentifyingVerbs: boolean;
  isEditingExistingArticle?: boolean;
}

const SentenceManagement = ({
  open,
  onOpenChange,
  sentences,
  onSentencesChange,
  onIdentifyVerbs,
  onProcessArticle,
  onEditVerbs,
  isProcessing,
  isIdentifyingVerbs,
  isEditingExistingArticle = false,
}: SentenceManagementProps) => {
  const { t } = useTranslation();
  const [editingSentenceIndex, setEditingSentenceIndex] = useState<
    number | null
  >(null);
  const [editingSentenceText, setEditingSentenceText] = useState('');

  const editSentence = (index: number, newText: string) => {
    const updated = [...sentences];
    updated[index] = newText.trim();
    onSentencesChange(updated);
  };

  const splitSentence = (index: number) => {
    const sentence = sentences[index];
    const midPoint = Math.floor(sentence.length / 2);
    const lastSpace = sentence.lastIndexOf(' ', midPoint);
    const splitPoint = lastSpace > 0 ? lastSpace : midPoint;

    const first = sentence.substring(0, splitPoint).trim();
    const second = sentence.substring(splitPoint).trim();

    const updated = [...sentences];
    updated.splice(index, 1, first, second);
    onSentencesChange(updated);
  };

  const mergeSentences = (index: number) => {
    if (index < sentences.length - 1) {
      const merged = `${sentences[index]} ${sentences[index + 1]}`;
      const updated = [...sentences];
      updated.splice(index, 2, merged);
      onSentencesChange(updated);
    }
  };

  const deleteSentence = (index: number) => {
    if (sentences.length > 1) {
      const updated = sentences.filter((_, i) => i !== index);
      onSentencesChange(updated);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('sentenceManagement.confirmAndEdit')}</DialogTitle>
          <DialogDescription>
            {t('sentenceManagement.reviewDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {sentences.map((sentence, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">
                  {t('translationEdit.sentence')} {index + 1}
                </span>
                {index === 0 && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {t('translationEdit.title')}
                  </span>
                )}
              </div>

              {editingSentenceIndex === index ? (
                <div className="space-y-2">
                  <Textarea
                    value={editingSentenceText}
                    onChange={e => setEditingSentenceText(e.target.value)}
                    className="min-h-[60px]"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        editSentence(index, editingSentenceText);
                        setEditingSentenceIndex(null);
                      }}
                    >
                      {t('actions.save')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingSentenceIndex(null)}
                    >
                      {t('actions.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm">{sentence}</p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingSentenceIndex(index);
                        setEditingSentenceText(sentence);
                      }}
                    >
                      <Edit2 className="h-3 w-3 mr-1" />
                      {t('actions.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => splitSentence(index)}
                    >
                      <Split className="h-3 w-3 mr-1" />
                      {t('sentenceManagement.split')}
                    </Button>
                    {index < sentences.length - 1 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => mergeSentences(index)}
                      >
                        <Merge className="h-3 w-3 mr-1" />
                        {t('sentenceManagement.mergeWithNext')}
                      </Button>
                    )}
                    {sentences.length > 1 && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteSentence(index)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        {t('actions.delete')}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('actions.cancel')}
          </Button>

          {isEditingExistingArticle ? (
            <>
              {onEditVerbs && (
                <Button onClick={onEditVerbs} variant="outline">
                  {t('sentenceManagement.editDifficultWords')}
                </Button>
              )}
              <Button onClick={onProcessArticle} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('sentenceManagement.processing')}
                  </>
                ) : (
                  t('sentenceManagement.updateTranslations')
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={onProcessArticle}
                disabled={isProcessing || isIdentifyingVerbs}
                variant="outline"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('sentenceManagement.translating')}
                  </>
                ) : (
                  t('sentenceManagement.skipVerbsTranslate')
                )}
              </Button>
              <Button
                onClick={onIdentifyVerbs}
                disabled={isProcessing || isIdentifyingVerbs}
              >
                {isIdentifyingVerbs ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('sentenceManagement.identifyingVerbs')}
                  </>
                ) : (
                  t('sentenceManagement.identifyDifficultWords')
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SentenceManagement;
